import { PayloadAction, createSelector, createSlice } from '@reduxjs/toolkit'
import type { RootState } from '@/redux'
import type {
  DashboardDomainAvailability,
  DashboardDomainRegistryItem,
  DashboardDomainRegistryStatus,
} from '@/types/DashboardDomainRegistry.types'

/**
 * dashboardDomainRegistrySlice — caches the BE response from
 * `GET /v1/dashboard-domains` for the lifetime of the SPA session.
 *
 * Why a slice (vs ad-hoc useState in FiltersBlock):
 *  - Multiple dashboard-creation surfaces will need the registry once T3.2
 *    extends past FiltersBlock (e.g. SourcePickerBlock for displayName,
 *    PanelsProposedBlock for build-error UNSUPPORTED_DOMAIN messages).
 *  - Persisted state is read-only — invalidation is page reload only
 *    (the BE registry is process-stable). No staleness logic.
 *  - Resume-after-reload: the bootstrap effect runs on every fresh
 *    `/dashboards/:id` mount; replaying `fetchDashboardDomains` on a fresh
 *    Redux store seeds the slice for any returning user.
 */

export interface DashboardDomainRegistryState {
  status: DashboardDomainRegistryStatus
  /** Picker-key → registry item. Built once on slice population. */
  byPickerKey: Record<string, DashboardDomainRegistryItem>
  /** Qualified-id → registry item. Built once on slice population. */
  byQualifiedId: Record<string, DashboardDomainRegistryItem>
  /** Last error text, if status === 'error'. */
  error: string | null
}

const initialState: DashboardDomainRegistryState = {
  status: 'idle',
  byPickerKey: {},
  byQualifiedId: {},
  error: null,
}

const dashboardDomainRegistrySlice = createSlice({
  name: 'dashboardDomainRegistry',
  initialState,
  reducers: {
    fetchStarted(state) {
      state.status = 'loading'
      state.error = null
    },
    fetchSucceeded(
      state,
      action: PayloadAction<DashboardDomainRegistryItem[]>,
    ) {
      const byPickerKey: Record<string, DashboardDomainRegistryItem> = {}
      const byQualifiedId: Record<string, DashboardDomainRegistryItem> = {}
      for (const raw of action.payload) {
        // Defensive normalisation: older tcs-api shapes (pre-Slice 3) may omit
        // `filters`. Default to [] so downstream consumers can iterate
        // unconditionally without optional chaining the entire surface.
        const item: DashboardDomainRegistryItem = {
          ...raw,
          filters: Array.isArray(raw.filters) ? raw.filters : [],
        }
        // Defensive: drop entries missing the required identity fields. The
        // FE never renders a domain card without a displayName (TDD §7.10).
        if (!item.displayName || !item.pickerKey || !item.qualifiedDomainId) {
          continue
        }
        byPickerKey[item.pickerKey] = item
        byQualifiedId[item.qualifiedDomainId] = item
      }
      state.status = 'ready'
      state.byPickerKey = byPickerKey
      state.byQualifiedId = byQualifiedId
      state.error = null
    },
    fetchFailed(state, action: PayloadAction<string>) {
      state.status = 'error'
      state.error = action.payload
    },
    /**
     * updateDomainAvailability — patches the `availability` field of a single
     * domain entry in the slice after a manual re-probe (Slice 2.3 §4e).
     *
     * Keyed by `pickerKey` (the era-agnostic key the FE uses throughout).
     * Silently no-ops if the pickerKey is not found — the worst outcome is a
     * stale card that refreshes on next registry fetch.
     */
    updateDomainAvailability(
      state,
      action: PayloadAction<{
        pickerKey: string
        availability: DashboardDomainAvailability
      }>,
    ) {
      const { pickerKey, availability } = action.payload
      const item = state.byPickerKey[pickerKey]
      if (item) {
        item.availability = availability
        // Keep byQualifiedId in sync.
        const byQualId = state.byQualifiedId[item.qualifiedDomainId]
        if (byQualId) {
          byQualId.availability = availability
        }
      }
    },
    /** Test-only — reset the slice to the initial state. */
    reset() {
      return initialState
    },
  },
})

export const dashboardDomainRegistryReducer =
  dashboardDomainRegistrySlice.reducer
export const dashboardDomainRegistryActions =
  dashboardDomainRegistrySlice.actions

// ─── Selectors ────────────────────────────────────────────────────────────────

export const selectDashboardDomainRegistryStatus = (
  state: RootState,
): DashboardDomainRegistryStatus => state.dashboardDomainRegistry.status

/**
 * Resolves an unqualified picker key (`'enrollment'`) to its qualified
 * dashboard-era id (`'campminder.enrollment-dashboard'`).
 *
 * Returns `null` if the registry has not yet loaded OR the picker key is
 * unknown. Callers MUST handle the null path — there is no synthesised
 * fallback (the previous hardcoded `qualify()` helper used
 * `` `campminder.${domainId}` `` as a last-resort fallback; that is gone now
 * because it produced era-mixed strings for unknown inputs).
 *
 * Era boundary discipline (CLAUDE.md): callers expecting a dashboard-era id
 * MUST use this selector, not a manual string concat. There is no
 * workflow-era variant of this selector — workflow-era resolution is BE-only.
 */
export const selectQualifiedDomainIdByPickerKey =
  (pickerKey: string) =>
  (state: RootState): string | null => {
    const item = state.dashboardDomainRegistry.byPickerKey[pickerKey]
    return item ? item.qualifiedDomainId : null
  }

export const selectDashboardDomainByPickerKey =
  (pickerKey: string) =>
  (state: RootState): DashboardDomainRegistryItem | null => {
    return state.dashboardDomainRegistry.byPickerKey[pickerKey] ?? null
  }

/**
 * Returns every registered dashboard-era domain sorted by `order` ascending,
 * with undefined-order entries pushed to the end and ties broken alphabetically
 * on `displayName`.
 *
 * Drives the picker grid + the FiltersBlock domain iteration. Disabled domains
 * are INCLUDED — the rendering layer decides how to present them (per TDD §6.2b
 * the picker shows them grayed out but they remain in the ordered list).
 *
 * v1 registry order (with metadata published by Slice 2): Enrollment (10) →
 * Financial (20) → Bunks (30) → Staff (40). Matches the prior FE hardcoded
 * order verbatim — zero visible reorder post-refactor.
 */
export const selectAllRegistryDomainsOrdered = (
  state: RootState,
): DashboardDomainRegistryItem[] => {
  const items = Object.values(state.dashboardDomainRegistry.byPickerKey)
  return [...items].sort((a, b) => {
    const aOrder = a.order ?? Number.MAX_SAFE_INTEGER
    const bOrder = b.order ?? Number.MAX_SAFE_INTEGER
    if (aOrder !== bOrder) return aOrder - bOrder
    return a.displayName.localeCompare(b.displayName)
  })
}

/**
 * Heuristic display-name fallback for a qualified id not registered (or for an
 * unqualified picker key). Splits on `.`, takes the last segment, strips the
 * `-dashboard` suffix if present, then capitalises the first letter.
 *
 * Pure helper — no Redux state. Exposed for tests + the parametrised selector.
 */
export const heuristicDomainDisplayName = (id: string): string => {
  const segment = id.includes('.') ? id.split('.').pop()! : id
  const stripped = segment.endsWith('-dashboard')
    ? segment.slice(0, -'-dashboard'.length)
    : segment
  return stripped.charAt(0).toUpperCase() + stripped.slice(1)
}

/**
 * Resolves a qualified dashboard-era id (or picker key, or unknown id) to a
 * human-readable display name. Hits the registry first; falls back to the
 * heuristic above for unknown ids.
 *
 * Used by Canvas + PanelCanvasV1 progress / error banners and any single-id
 * resolution path. For `.map()`-time bulk resolution prefer
 * `selectResolveDomainDisplayName` — it returns one bound resolver instead of
 * forcing a hook call per id.
 */
export const selectDomainDisplayNameByQualifiedId =
  (qualifiedId: string) =>
  (state: RootState): string => {
    const slice = state.dashboardDomainRegistry
    const direct = slice?.byQualifiedId[qualifiedId]
    if (direct) return direct.displayName
    const byPicker = slice?.byPickerKey[qualifiedId]
    if (byPicker) return byPicker.displayName
    return heuristicDomainDisplayName(qualifiedId)
  }

/**
 * Returns a resolver function bound to current state — usable inside `.map()`
 * callsites that need to translate an id to a display name without calling
 * `useSelector` per id (illegal — hooks cannot run in callbacks).
 *
 * Selector identity changes only when the registry maps change, so memoised
 * components stay stable.
 */
/**
 * Returns a stable resolver function `(id: string) => string` that maps a
 * domain id (qualified or picker-key) to a human display name.
 *
 * Built with `createSelector` so the resolver is memoised on the
 * `byQualifiedId` + `byPickerKey` map identities — the returned function
 * reference is the same object across dispatches as long as neither map
 * changes. Consumers on `Canvas`, `PanelCanvasV1`, and `ChatOverlay` will
 * not re-render from unrelated Redux dispatches.
 *
 * Selector identity changes only when the registry maps change, so memoised
 * components stay stable.
 */
export const selectResolveDomainDisplayName = createSelector(
  [
    // Defensive read — older tests may construct a partial store without the
    // dashboardDomainRegistry slice; in that case fall through to the heuristic.
    (s: RootState) => s.dashboardDomainRegistry?.byQualifiedId ?? {},
    (s: RootState) => s.dashboardDomainRegistry?.byPickerKey ?? {},
  ],
  (byQualifiedId, byPickerKey) =>
    (id: string): string => {
      const direct = byQualifiedId[id]
      if (direct) return direct.displayName
      const byPicker = byPickerKey[id]
      if (byPicker) return byPicker.displayName
      return heuristicDomainDisplayName(id)
    },
)

/**
 * selectDomainAvailability — resolves the `availability` block for a domain
 * by its picker key (e.g. `'staff'`).
 *
 * Returns `undefined` when the registry has not loaded, the pickerKey is
 * unknown, or the BE omitted the field (no credential for that domain's
 * appCode). Callers MUST treat `undefined` as fail-open (render as available).
 *
 * Dashboard-era only. Do not use for workflow-era surfaces.
 */
export const selectDomainAvailability =
  (pickerKey: string) =>
  (state: RootState): DashboardDomainAvailability | undefined => {
    return state.dashboardDomainRegistry.byPickerKey[pickerKey]?.availability
  }
